/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, Output, signal, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	LucideCloudUpload,
	LucideDownload,
	LucideDynamicIcon,
	LucideFileUp,
	LucideImport,
	LucideInbox,
	LucideMoveDown,
	LucideUpload,
} from '@lucide/angular';
import {
	DEFAULT_UPLOAD_CONFIGURATION,
	ExistingUpload,
	UploadConfiguration,
	UploadIconLibrary,
	UploadItem,
	UploadValidationFailure,
	UploadValue,
	UploadVariant,
} from './upload.models';
import { OrbitIconComponent } from '../orbit-icon/orbit-icon.component';

interface FileTypeDefinition {
	display: string;
	mimeTypes: string[];
}

const FILE_TYPES: Record<string, FileTypeDefinition> = {
	svg: { display: 'SVG', mimeTypes: ['image/svg+xml'] },
	png: { display: 'PNG', mimeTypes: ['image/png'] },
	jpg: { display: 'JPG', mimeTypes: ['image/jpeg'] },
	jpeg: { display: 'JPG', mimeTypes: ['image/jpeg'] },
	webp: { display: 'WEBP', mimeTypes: ['image/webp'] },
	pdf: { display: 'PDF', mimeTypes: ['application/pdf'] },
	docx: { display: 'DOCX', mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
};

const LUCIDE_UPLOAD_ICONS: Record<string, any> = {
	upload: LucideUpload,
	cloudupload: LucideCloudUpload,
	uploadcloud: LucideCloudUpload,
	fileup: LucideFileUp,
	import: LucideImport,
	inbox: LucideInbox,
	movedown: LucideMoveDown,
	download: LucideDownload,
};

@Component({
	selector: 'kaleida-upload',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatTooltipModule, LucideDynamicIcon, OrbitIconComponent],
	templateUrl: './upload.component.html',
	styleUrls: ['./upload.component.scss'],
	providers: [
		{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UploadComponent), multi: true },
		{ provide: NG_VALIDATORS, useExisting: forwardRef(() => UploadComponent), multi: true },
	],
})
export class UploadComponent implements ControlValueAccessor, Validator, OnDestroy {
	@ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

	@Input()
	set configuration(value: UploadConfiguration | null | undefined) {
		const variant = value?.variant ?? DEFAULT_UPLOAD_CONFIGURATION.variant;
		const configuredMaximumFileCount = Math.max(1, Math.floor(value?.maximumFileCount ?? DEFAULT_UPLOAD_CONFIGURATION.maximumFileCount));
		const maximumFileCount = variant === UploadVariant.Compact ? 1 : configuredMaximumFileCount;
		const maximumFileSizeMb = Math.max(0, value?.maximumFileSizeMb ?? DEFAULT_UPLOAD_CONFIGURATION.maximumFileSizeMb);
		const previewBackground = value?.previewBackground ?? value?.selectedBackgroundColour ?? DEFAULT_UPLOAD_CONFIGURATION.previewBackground;
		this.configState.set({ ...DEFAULT_UPLOAD_CONFIGURATION, ...value, variant, maximumFileCount, maximumFileSizeMb, previewBackground });
	}

	@Output() readonly filesSelected = new EventEmitter<File[]>();
	@Output() readonly fileRemoved = new EventEmitter<UploadItem>();
	@Output() readonly selectionChanged = new EventEmitter<UploadItem[]>();
	@Output() readonly activeImageChanged = new EventEmitter<UploadItem | null>();
	@Output() readonly validationFailed = new EventEmitter<UploadValidationFailure>();

	readonly UploadVariant = UploadVariant;
	readonly UploadIconLibrary = UploadIconLibrary;
	readonly configState = signal<Required<UploadConfiguration>>({ ...DEFAULT_UPLOAD_CONFIGURATION });
	readonly items = signal<UploadItem[]>([]);
	readonly activeKey = signal<string | null>(null);
	readonly validationMessage = signal('');
	readonly pointerOver = signal(false);
	readonly dragOver = signal(false);
	readonly disabled = signal(false);

	readonly isMultiple = computed(() => this.configState().maximumFileCount > 1);
	readonly isCompact = computed(() => this.configState().variant === UploadVariant.Compact);
	readonly isImage = computed(() => this.configState().variant === UploadVariant.Image);
	readonly activeItem = computed(() => this.items().find((item) => item.key === this.activeKey()) ?? null);
	readonly showDropState = computed(() => !this.disabled() && (this.dragOver() || this.pointerOver()) && this.canShowDropState());
	readonly activeIconLibrary = computed(() => this.showDropState() ? this.configState().dropIconLibrary : this.configState().uploadIconLibrary);
	readonly activeIconName = computed(() => this.showDropState() ? this.configState().dropIcon : this.configState().uploadIcon);
	readonly activeLucideIcon = computed(() => {
		const key = this.activeIconName().replace(/[\s_-]/g, '').toLowerCase();
		return LUCIDE_UPLOAD_ICONS[key] ?? (this.showDropState() ? LucideMoveDown : LucideCloudUpload);
	});
	readonly normalisedExtensions = computed(() => this.normaliseExtensions(this.configState().allowedExtensions));
	readonly accept = computed(() => {
		const normalisedExtensions = this.normalisedExtensions();
		const expandedExtensions = normalisedExtensions.flatMap((extension) => extension === 'jpg' || extension === 'jpeg' ? ['jpg', 'jpeg'] : [extension]);
		const extensions = [...new Set(expandedExtensions)].map((extension) => `.${extension}`);
		const configuredMimeTypes = this.configState().allowedMimeTypes.map((mimeType) => mimeType.trim().toLowerCase()).filter(Boolean);
		const inferredMimeTypes = normalisedExtensions.flatMap((extension) => FILE_TYPES[extension]?.mimeTypes ?? []);
		const mimeTypes = configuredMimeTypes.length > 0 ? configuredMimeTypes : inferredMimeTypes;
		return [...new Set([...extensions, ...mimeTypes])].join(',');
	});
	readonly restrictions = computed(() => {
		const config = this.configState();
		const types = [...new Set(this.normalisedExtensions().map((extension) => FILE_TYPES[extension]?.display ?? extension.toUpperCase()))].join(' · ');
		const size = config.maximumFileSizeMb > 0 ? `max ${this.formatLimit(config.maximumFileSizeMb)}${config.maximumFileCount > 1 ? ' each' : ''}` : '';
		const count = config.maximumFileCount > 1 ? `max ${config.maximumFileCount} files` : '';
		return [types, size, count].filter(Boolean).join(' · ');
	});
	readonly descriptionIds = computed(() => {
		const ids: string[] = [];
		if (this.configState().helpText) ids.push(`${this.controlId}-help`);
		if (this.configState().supportingMessage) ids.push(`${this.controlId}-support`);
		if (this.restrictions()) ids.push(`${this.controlId}-restrictions`);
		if (this.validationMessage()) ids.push(`${this.controlId}-error`);
		return ids.join(' ');
	});

	readonly controlId = `kaleida-upload-${UploadComponent.nextId++}`;
	private static nextId = 0;
	private dragDepth = 0;
	private onChange: (value: UploadValue) => void = () => {};
	private onTouched: () => void = () => {};
	private onValidatorChange: () => void = () => {};

	writeValue(value: UploadValue): void {
		this.releaseObjectUrls(this.items());
		const values = value == null ? [] : Array.isArray(value) ? value : [value];
		const nextItems = values.slice(0, this.configState().maximumFileCount).map((entry) => this.toItem(entry));
		this.items.set(nextItems);
		this.activeKey.set(nextItems[0]?.key ?? null);
		this.validationMessage.set('');
		this.clearNativeInput();
		this.onValidatorChange();
	}

	registerOnChange(fn: (value: UploadValue) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	registerOnValidatorChange(fn: () => void): void {
		this.onValidatorChange = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled);
		if (isDisabled) this.resetInteractionState();
	}

	validate(_control: AbstractControl): ValidationErrors | null {
		return this.configState().required && this.items().length === 0 ? { required: true } : null;
	}

	openPicker(): void {
		if (this.disabled()) return;
		this.onTouched();
		this.fileInput?.nativeElement.click();
	}

	onControlClick(): void {
		if (this.isCompact() && this.items().length > 0) return;
		this.openPicker();
	}

	openPickerFromChild(event: Event): void {
		event.stopPropagation();
		this.openPicker();
	}

	onControlKeydown(event: KeyboardEvent): void {
		if (event.target !== event.currentTarget) return;
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		this.openPicker();
	}

	onInputChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.processFiles(Array.from(input.files ?? []), true);
		input.value = '';
	}

	onPointerEnter(): void {
		if (this.canShowDropState()) this.pointerOver.set(true);
	}

	onPointerLeave(): void {
		this.pointerOver.set(false);
	}

	onDragEnter(event: DragEvent): void {
		event.preventDefault();
		if (!this.canAcceptDrop(event)) return;
		this.dragDepth++;
		this.dragOver.set(true);
	}

	onDragOver(event: DragEvent): void {
		event.preventDefault();
		if (this.canAcceptDrop(event) && event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		if (this.dragDepth > 0) this.dragDepth--;
		if (this.dragDepth === 0) this.dragOver.set(false);
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		const canAccept = this.canAcceptDrop(event);
		this.resetInteractionState();
		if (!canAccept) return;
		this.onTouched();
		this.processFiles(Array.from(event.dataTransfer?.files ?? []), false);
	}

	selectItem(item: UploadItem, event?: Event): void {
		event?.stopPropagation();
		if (this.disabled() || !this.isImage() || this.activeKey() === item.key) return;
		this.activeKey.set(item.key);
		this.activeImageChanged.emit(item);
		this.onTouched();
	}

	removeItem(item: UploadItem, event: Event): void {
		event.stopPropagation();
		if (this.disabled()) return;
		const previousItems = this.items();
		const removedIndex = previousItems.findIndex((candidate) => candidate.key === item.key);
		if (removedIndex < 0) return;
		const wasActive = this.activeKey() === item.key;
		const nextItems = previousItems.filter((candidate) => candidate.key !== item.key);
		this.releaseObjectUrl(item);
		this.items.set(nextItems);
		if (wasActive) {
			const nextActive = nextItems[removedIndex] ?? nextItems[removedIndex - 1] ?? null;
			this.activeKey.set(nextActive?.key ?? null);
			this.activeImageChanged.emit(nextActive);
		}
		this.validationMessage.set('');
		this.fileRemoved.emit(item);
		this.emitValueChange();
		this.onTouched();
	}

	formatFileSize(bytes: number): string {
		if (!bytes) return '0 KB';
		if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
	}

	displayType(item: UploadItem): string {
		return FILE_TYPES[item.extension]?.display ?? item.extension.toUpperCase();
	}

	ngOnDestroy(): void {
		this.releaseObjectUrls(this.items());
	}

	private processFiles(files: File[], fromPicker: boolean): void {
		if (this.disabled() || files.length === 0) return;
		const config = this.configState();
		// Single-file controls replace through either picker or drop. Compact controls
		// deliberately reject drops while populated before reaching this method.
		let nextItems = config.maximumFileCount === 1 ? [] : [...this.items()];
		const acceptedFiles: File[] = [];
		let latestMessage = '';

		for (const file of files) {
			const failure = this.validateFile(file, nextItems);
			if (failure) {
				latestMessage = failure.message;
				this.validationFailed.emit(failure);
				continue;
			}
			if (nextItems.length >= config.maximumFileCount) {
				const failure = { file, reason: 'fileCount' as const, message: `A maximum of ${config.maximumFileCount} files can be selected.` };
				latestMessage = failure.message;
				this.validationFailed.emit(failure);
				continue;
			}
			const item = this.toItem(file);
			nextItems.push(item);
			acceptedFiles.push(file);
		}

		if (acceptedFiles.length > 0) {
			if (config.maximumFileCount === 1) this.releaseObjectUrls(this.items());
			this.items.set(nextItems);
			const active = nextItems[nextItems.length - 1];
			this.activeKey.set(active.key);
			this.filesSelected.emit(acceptedFiles);
			this.activeImageChanged.emit(active);
			this.emitValueChange();
		}
		this.validationMessage.set(latestMessage);
	}

	private validateFile(file: File, currentItems: UploadItem[]): UploadValidationFailure | null {
		const extensions = this.normalisedExtensions();
		const extension = this.getExtension(file.name);
		if (extensions.length > 0 && !this.isAllowedExtension(extension, extensions)) {
			return { file, reason: 'fileType', message: `Select a ${this.joinTypes(extensions)} file.` };
		}

		const configuredMimeTypes = this.configState().allowedMimeTypes.map((type) => type.trim().toLowerCase()).filter(Boolean);
		const inferredMimeTypes = FILE_TYPES[extension]?.mimeTypes ?? [];
		const allowedMimeTypes = configuredMimeTypes.length > 0 ? configuredMimeTypes : inferredMimeTypes;
		if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type.toLowerCase())) {
			return { file, reason: 'fileType', message: `Select a ${this.joinTypes(extensions)} file.` };
		}

		const maxBytes = this.configState().maximumFileSizeMb * 1024 * 1024;
		if (maxBytes > 0 && file.size > maxBytes) {
			return { file, reason: 'fileSize', message: `The file must be ${this.formatLimit(this.configState().maximumFileSizeMb)} or smaller.` };
		}

		if (currentItems.some((item) => item.file && item.name === file.name && item.size === file.size && item.file.lastModified === file.lastModified)) {
			return { file, reason: 'duplicate', message: `${file.name} has already been selected.` };
		}
		return null;
	}

	private toItem(value: File | ExistingUpload): UploadItem {
		if (value instanceof File) {
			return {
				key: `file:${value.name}:${value.size}:${value.lastModified}`,
				name: value.name,
				extension: this.getExtension(value.name),
				size: value.size,
				mimeType: value.type,
				previewUrl: this.configState().variant === UploadVariant.Image ? URL.createObjectURL(value) : undefined,
				file: value,
			};
		}
		return {
			key: `existing:${value.id}`,
			name: value.name,
			extension: this.getExtension(value.name),
			size: value.size ?? 0,
			mimeType: value.mimeType ?? '',
			previewUrl: value.previewUrl,
			existing: value,
		};
	}

	private emitValueChange(): void {
		const values = this.items().map((item) => item.file ?? item.existing).filter(Boolean) as Array<File | ExistingUpload>;
		const value: UploadValue = this.configState().maximumFileCount === 1 ? values[0] ?? null : values;
		this.onChange(value);
		this.selectionChanged.emit([...this.items()]);
		this.onValidatorChange();
	}

	private normaliseExtensions(extensions: string[]): string[] {
		return [...new Set((extensions ?? []).map((extension) => extension.trim().toLowerCase().replace(/^\*?\./, '')).filter(Boolean))];
	}

	private joinTypes(extensions: string[]): string {
		const names = [...new Set(extensions.map((extension) => FILE_TYPES[extension]?.display ?? extension.toUpperCase()))];
		if (names.length < 2) return names[0] ?? 'supported';
		return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
	}

	private getExtension(filename: string): string {
		const index = filename.lastIndexOf('.');
		return index >= 0 ? filename.slice(index + 1).toLowerCase() : '';
	}

	private isAllowedExtension(extension: string, configuredExtensions: string[]): boolean {
		if (configuredExtensions.includes(extension)) return true;
		return (extension === 'jpg' && configuredExtensions.includes('jpeg')) || (extension === 'jpeg' && configuredExtensions.includes('jpg'));
	}

	private formatLimit(megabytes: number): string {
		return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
	}

	private canShowDropState(): boolean {
		return !(this.isCompact() && this.items().length > 0);
	}

	private canAcceptDrop(event: DragEvent): boolean {
		const hasFiles = !event.dataTransfer?.types || Array.from(event.dataTransfer.types).includes('Files');
		return !this.disabled() && this.canShowDropState() && hasFiles;
	}

	private resetInteractionState(): void {
		this.dragDepth = 0;
		this.dragOver.set(false);
		this.pointerOver.set(false);
	}

	private clearNativeInput(): void {
		if (this.fileInput) this.fileInput.nativeElement.value = '';
	}

	private releaseObjectUrls(items: UploadItem[]): void {
		items.forEach((item) => this.releaseObjectUrl(item));
	}

	private releaseObjectUrl(item: UploadItem): void {
		if (item.file && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
	}
}
